import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Prompt, useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import TextField from '@mui/material/TextField'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import SearchIcon from '@mui/icons-material/Search'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Add from '@mui/icons-material/Add'

import Title from '../../component/Title'
import { LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_SEARCH_BUTTON_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup, { FORM_COMPACT_ACTION_BUTTON_SX, FORM_WIDE_UPLOAD_BUTTON_SX } from '../../component/BottomButtonGroup'
import Spiner from '../../component/Spiner'
import Pagination from '../../component/Pagination'
import ListEmptyState from '../../component/ListEmptyState'
import { FORM_IMAGE_PREVIEW_FRAME_SX, FORM_PANEL_SX } from '../../component/formSurfaceStyles'
import { getLocationState } from '../../utils/listPageState'
import {
    applyAppCoupangBannerDraft,
    applyAppCoupangBannerFieldChange,
    APP_COUPANG_BANNER_IMAGE_FIELD_CONFIGS,
    APP_COUPANG_BANNER_IMAGE_FILE_ACCEPT,
    APP_COUPANG_BANNER_IMAGE_FILE_ERROR_MESSAGE,
    clearAppCoupangBannerDraft,
    createAppCoupangBannerDraftPayload,
    createDefaultAppCoupangBannerParams,
    createEditAppCoupangBannerDraftKey,
    handleAppCoupangBannerBackNavigation,
    handleAppCoupangBannerImageUpload,
    moveToAppCoupangBannerList,
    prepareAppCoupangBannerSubmit,
    readAppCoupangBannerDraft,
    saveAppCoupangBannerDraft,
} from './appCoupangBannerDraftUtils'
import {
    APP_COUPANG_BANNER_IMAGE_LEAVE_MESSAGE,
    hasPendingAppCoupangBannerImage,
    usePendingAppCoupangBannerImageUnloadGuard,
} from './appCoupangBannerLeaveGuard'

const PANEL_SX = FORM_PANEL_SX

const IMAGE_PREVIEW_FRAME_SX = {
    ...FORM_IMAGE_PREVIEW_FRAME_SX,
    width: '100%',
    height: { xs: 220, md: 280 },
    mb: 1.5,
}

const IMAGE_PREVIEW_SX = {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
}

const getImageFileName = (imageFile) => {
    if (!imageFile || typeof imageFile !== 'object') {
        return ''
    }

    return imageFile.name || ''
}

function FormSection({ title, description, children }) {
    return (
        <Paper variant="outlined" sx={PANEL_SX}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {description}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {children}
            </Box>
        </Paper>
    )
}

function CoupangBannerImageUploadPanel({
    fieldConfig,
    imageSrc,
    imageFile,
    disabled,
    onUploadClick,
}) {
    const imageFileName = getImageFileName(imageFile)

    return (
        <Paper variant="outlined" sx={PANEL_SX}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {fieldConfig.previewAlt}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    App 최저가 배너 화면에 노출되는 이미지를 확인하고 교체합니다.
                </Typography>
            </Box>
            <Box sx={IMAGE_PREVIEW_FRAME_SX}>
                {imageSrc ? (
                    <Box
                        component="img"
                        src={imageSrc}
                        alt={`${fieldConfig.previewAlt} 미리보기`}
                        sx={IMAGE_PREVIEW_SX}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        이미지를 선택하면 미리보기가 표시됩니다.
                    </Typography>
                )}
            </Box>
            <FormControl fullWidth>
                <TextField
                    inputProps={{ accept: APP_COUPANG_BANNER_IMAGE_FILE_ACCEPT }}
                    style={{ display: 'none' }}
                    name={fieldConfig.fieldName}
                    id={fieldConfig.inputId}
                    type="file"
                    disabled={disabled}
                    onChange={onUploadClick}
                />
                <Box component="label" htmlFor={fieldConfig.inputId} sx={{ display: 'block' }}>
                    <Button
                        fullWidth
                        variant="contained"
                        color="success"
                        component="span"
                        startIcon={<Add />}
                        sx={FORM_WIDE_UPLOAD_BUTTON_SX}
                        disabled={disabled}
                    >
                        {fieldConfig.uploadLabel}
                    </Button>
                </Box>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {imageFileName ? `선택한 파일: ${imageFileName}` : '저장 전 미리보기로 이미지가 올바른지 확인하세요.'}
            </Typography>
        </Paper>
    )
}

export default function AppCoupangBanner(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.coupangBannerId
    const listCurPage = locationState.curPage || 1
    const listSearchText = locationState.searchText
    const listSearchSelect = locationState.searchSelect
    const listSearch = location.search || ''
    const draftKey = createEditAppCoupangBannerDraftKey(id)
    const storedDraftRef = useRef(readAppCoupangBannerDraft(draftKey))
    const [totalCount, setTotalCount] = useState(0)
    const [curPage, setCurPage] = useState(() => {
        const storedDraft = storedDraftRef.current
        return storedDraft?.pickerPage ? Number(storedDraft.pickerPage) || 1 : 1
    })
    const offset = 5
    const [datas, setDatas] = useState([])
    const [loading, setLoading] = useState(true)
    const [hasDetail, setHasDetail] = useState(false)
    const [saving, setSaving] = useState(false)
    const [draftReady, setDraftReady] = useState(false)
    const [params, setParams] = useState(() => createDefaultAppCoupangBannerParams())
    const [image, setImage] = useState('')
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const [searchProductName, setSearchProductName] = useState(() => storedDraftRef.current?.pickerSearchText || '')
    const searchProductNameRef = useRef(storedDraftRef.current?.pickerSearchText || '')
    const [productSearchSelect] = useState({
        keyword1: '',
    })
    const initialDraftSnapshotRef = useRef('')
    const [navigationUnlocked, setNavigationUnlocked] = useState(false)
    const leaveGuardEnabled = hasPendingAppCoupangBannerImage(params) && !saving && !navigationUnlocked
    const imageFieldConfigs = APP_COUPANG_BANNER_IMAGE_FIELD_CONFIGS

    usePendingAppCoupangBannerImageUnloadGuard(leaveGuardEnabled)

    const getData = useCallback(async () => {
        if (!id) {
            const errorMessage = '필수 파라미터가 없어 App 최저가 배너 상세를 불러올 수 없습니다.'
            setParams(createDefaultAppCoupangBannerParams())
            setImage('')
            setHasDetail(false)
            setDraftReady(false)
            setDetailErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
            setLoading(false)
            return
        }

        setLoading(true)
        setHasDetail(false)
        setDraftReady(false)
        setDetailErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/appCoupangBanner/getDetail', { coupangBannerId: id })
            if (data.code === 200) {
                const baseParams = data.data || {}

                if (!baseParams.coupangBannerId) {
                    setParams(createDefaultAppCoupangBannerParams())
                    setImage('')
                    setDetailErrorMessage('App 최저가 배너 상세 정보를 찾을 수 없습니다.')
                    setLoading(false)
                    return
                }

                const restoredDraft = applyAppCoupangBannerDraft({
                    baseParams,
                    basePickerPage: 1,
                    basePickerSearchText: '',
                    draft: storedDraftRef.current,
                })

                initialDraftSnapshotRef.current = JSON.stringify(createAppCoupangBannerDraftPayload({
                    params: baseParams,
                    pickerPage: 1,
                    pickerSearchText: '',
                }))

                setParams(restoredDraft.params)
                setCurPage(restoredDraft.pickerPage)
                searchProductNameRef.current = restoredDraft.pickerSearchText
                setSearchProductName(restoredDraft.pickerSearchText)
                setImage(baseParams.image === null ? '/images/noimage.jpg' : `${process.env.REACT_APP_AWS_S3_URL}/${baseParams.image}`)
                setHasDetail(true)
                setDetailErrorMessage('')
                setDraftReady(true)
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || 'App 최저가 배너 상세를 불러오지 못했습니다.'
                setParams(createDefaultAppCoupangBannerParams())
                setImage('')
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = 'App 최저가 배너 상세를 불러오지 못했습니다.'
            setParams(createDefaultAppCoupangBannerParams())
            setImage('')
            setDetailErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [history, id])

    useEffect(() => {
        getData()
    }, [getData])

    const getCoupangData = useCallback(async () => {
        try {
            const { data } = await API.post('/api/v1/admin/appCoupangBanner/getCoupangList', {
                page: curPage,
                offset: offset,
                searchText: searchProductNameRef.current,
                searchSelect: productSearchSelect
            })
            if (data.code === 200) {
                setTotalCount(data.data.count)
                setDatas(data.data.rows)
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('쿠팡 상품 목록을 불러오지 못했습니다.')
        }
    }, [curPage, history, productSearchSelect])

    useEffect(() => {
        getCoupangData()
    }, [getCoupangData])

    useEffect(() => {
        if (!draftReady || !hasDetail) {
            return
        }

        // 최저가 배너 수정은 선택 상품과 쿠팡 상품 검색 상태를 sessionStorage에 함께 보존한다.
        const nextDraft = createAppCoupangBannerDraftPayload({
            params,
            pickerPage: curPage,
            pickerSearchText: searchProductName,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearAppCoupangBannerDraft(draftKey)
            return
        }

        saveAppCoupangBannerDraft(draftKey, nextDraft)
    }, [curPage, draftKey, draftReady, hasDetail, params, searchProductName])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const moveToList = (snackbarState) => {
        moveToAppCoupangBannerList({
            draftKey,
            clearDraft: clearAppCoupangBannerDraft,
            setNavigationUnlocked,
            history,
            listSearch,
            snackbarState,
            curPage: listCurPage,
            searchText: listSearchText,
            searchSelect: listSearchSelect,
        })
    }

    const handleBackClick = (event) => {
        handleAppCoupangBannerBackNavigation({
            event,
            leaveGuardEnabled,
            draftKey,
            clearDraft: clearAppCoupangBannerDraft,
            confirmLeave: () => window.confirm(APP_COUPANG_BANNER_IMAGE_LEAVE_MESSAGE),
            moveToList,
        })
    }

    const handleUpdateField = (e) => {
        setParams((prev) => applyAppCoupangBannerFieldChange({
            params: prev,
            name: e.target.name,
            value: e.target.value,
        }))
    }

    const handleUploadClick = (e) => {
        handleAppCoupangBannerImageUpload({
            event: e,
            setParams,
            setImage,
            onInvalidFile: (message = APP_COUPANG_BANNER_IMAGE_FILE_ERROR_MESSAGE) => {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(message)
            },
        })
    }

    const updateData = async (formData) => {
        try {
            setSaving(true)

            const { data } = await API({
                method: 'post',
                url: '/api/v1/admin/appCoupangBanner/update',
                data: formData,
                headers: { "Content-Type": "multipart/form-data" }
            })

            if (data.code === 200) {
                // 저장 후에도 운영자가 보던 최저가 배너 목록 조건으로 그대로 복귀시킨다.
                moveToList({
                    severity: 'success',
                    message: data.message,
                })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('App 최저가 배너를 저장하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        const { nextParams, errorMessage, formData } = prepareAppCoupangBannerSubmit({
            params,
            coupangBannerId: id,
        })

        setParams(nextParams)

        if (errorMessage) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
            return
        }

        updateData(formData)
    }

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="App 최저가 배너 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" path="/appCoupangBanners" search={listSearch} visible={false} curPage={listCurPage} searchText={listSearchText} searchSelect={listSearchSelect} onBackClick={() => clearAppCoupangBannerDraft(draftKey)} backLabel="목록으로"/>
            )}
            sx={{ mt: 2 }}
        />
    )

    const searchButtonClick = () => {
        if (curPage === 1) {
            getCoupangData()
            return
        }

        setCurPage(1)
    }

    const chooseButtonClick = (coupangId, productName) => {
        setParams((prev) => ({
            ...prev,
            coupangId: coupangId,
            productName: productName,
        }))
    }

    if (loading) {
        return (
            <Grid item xs={12}>
                <Spiner/>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Grid>
        )
    }

    if (!hasDetail) {
        return (
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>App 최저가 배너 수정</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            </Grid>
        )
    }

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>App 최저가 배너 수정</Title>
                <Prompt when={leaveGuardEnabled} message={APP_COUPANG_BANNER_IMAGE_LEAVE_MESSAGE} />
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', opacity: saving ? 0.5 : 1, pointerEvents: saving ? 'none' : 'auto' }}>
                    <ListFilterBar>
                        <ListFilterForm onSubmit={(e) => {
                            e.preventDefault()
                            searchButtonClick()
                        }}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="상품명"
                                type="search"
                                name="searchProductName"
                                value={searchProductName}
                                disabled={saving}
                                onChange={(e) => {
                                    searchProductNameRef.current = e.target.value
                                    setSearchProductName(e.target.value)
                                }}
                            />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={saving}><SearchIcon /></IconButton>
                        </ListFilterForm>
                    </ListFilterBar>

                    <Table size="small">
                        <caption>Total Count: {totalCount}</caption>
                        <TableHead>
                            <TableRow>
                                <TableCell>No</TableCell>
                                <TableCell>상품이미지</TableCell>
                                <TableCell>카테고리</TableCell>
                                <TableCell>하위카테고리</TableCell>
                                <TableCell>상품명</TableCell>
                                <TableCell align="center">url</TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {datas.map((row) => (
                            <TableRow key={row.coupangId} sx={{ backgroundColor: row.coupangId === params.coupangId ? 'silver' : '' }}>
                                <TableCell>{row.rownum}</TableCell>
                                <TableCell><img className="listImg" src={row.productImage} alt="" /></TableCell>
                                <TableCell>{row.keyword1}</TableCell>
                                <TableCell>{row.keyword2}</TableCell>
                                <TableCell>{row.productName}</TableCell>
                                <TableCell>
                                    <Button
                                        onClick={() => {
                                            window.open(row.landingUrl ,'_blank', 'noopener noreferrer')
                                        }}
                                        disabled={saving}
                                    >
                                        Link
                                    </Button>
                                </TableCell>
                                <TableCell align="right">
                                    <Button
                                        variant="contained"
                                        color="success"
                                        size="small"
                                        sx={FORM_COMPACT_ACTION_BUTTON_SX}
                                        onClick={() => chooseButtonClick(row.coupangId, row.productName)}
                                        disabled={saving}
                                    >
                                        선택
                                    </Button>
                                </TableCell>
                            </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                </Paper>

                <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }}>
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        {imageFieldConfigs.map((fieldConfig) => (
                            <Grid key={fieldConfig.inputId} item xs={12} md={5}>
                                <CoupangBannerImageUploadPanel
                                    fieldConfig={fieldConfig}
                                    imageSrc={image || '/images/noimage.jpg'}
                                    imageFile={params[fieldConfig.fieldName]}
                                    disabled={saving}
                                    onUploadClick={handleUploadClick}
                                />
                            </Grid>
                        ))}
                        <Grid item xs={12} md={7}>
                            <FormSection
                                title="배너 정보"
                                description="선택한 쿠팡 상품과 배너 노출 순서를 확인합니다."
                            >
                                <FormControl fullWidth>
                                    <TextField
                                        label="상품명"
                                        required
                                        id="productName"
                                        name="productName"
                                        multiline
                                        rows={2}
                                        value={params.productName || ''}
                                        size="small"
                                        disabled={saving}
                                        InputProps={{ readOnly: true }}
                                    />
                                </FormControl>
                                <Box>
                                    <FormControl sx={{ width: 120 }}>
                                        <TextField
                                            type="number"
                                            InputProps={{ inputProps: { min: 0, max: 99 } }}
                                            label="노출순서"
                                            id="order"
                                            name="order"
                                            value={params.order}
                                            disabled={saving}
                                            onChange={handleUpdateField}
                                            size="small"
                                        />
                                    </FormControl>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                        숫자가 작을수록 먼저 노출됩니다. 입력 범위는 0~99입니다.
                                    </Typography>
                                </Box>
                            </FormSection>
                        </Grid>
                    </Grid>
                    <BottomButtonGroup
                        path="/appCoupangBanners"
                        search={listSearch}
                        visible={true}
                        curPage={listCurPage}
                        searchText={listSearchText}
                        searchSelect={listSearchSelect}
                        submitDisabled={saving}
                        submitLabel={saving ? '저장 중...' : '저장'}
                        onBackClick={handleBackClick}
                    />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
